/* eslint-disable react-hooks/exhaustive-deps */

import { FolderOutlineOpenIcon } from '@tableau/icons/FolderOutlineOpenIcon';
import { NestedItemsFractalRowStore } from '@tableau/nested-project-explorer';
import { TreeInstance, TreeItem, TreeWidget } from '@tableau/widgets';
import React, { useMemo } from 'react';

import { Location } from '~commonInfra/utils/Location';
import { Messages } from '~src/codegen/Localize';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { Redux } from '~src/settings/general-settings/utils';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { useEPT } from '~src/spacesPlatform/hooks/useEPT';
import { selectLayoutType } from '~src/spacesPlatform/selectors/LayoutTypeSelector';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { getSiteName } from '~src/uploadContent/utils/ProjectExplorerEffects';

import { IDestinationProject } from '../ServerApi/types/IDestinationProject';
import { projectTreeFeatureName, ProjectTreeService } from '../services/ProjectTreeService';
import { ProjectHooks } from '../services/Queries';
import { ProjectDefinition } from './contentDefinitions/ProjectDefinition';
import { projectTreeNodeStyle, projectTreeNodeTextContainerStyle, projectTreeNodeTextStyle, projectTreeStyle } from './styles/ProjectTreeStyles';

export type ProjectTreeItem = TreeItem & { projectName: string, luid: string };

const getContent = (projectName: string) => (
    <div className={projectTreeNodeStyle} title={projectName}>
        <FolderOutlineOpenIcon size={18} />
        <div className={projectTreeNodeTextContainerStyle}>
            <span className={projectTreeNodeTextStyle}>
                {projectName}
            </span>
        </div>
    </div>
);

function convertProjectsToProjectTreeItems(projects: IDestinationProject[]) {
    const projectTreeItems: ProjectTreeItem[] | null = (projects && projects.length > 0) ? [] : null;
    const projectById: Record<string, IDestinationProject> = {};
    let addPermissonsRequiredProjectItem: boolean = false;

    projects && projects.forEach(project => {
        projectById[project.id] = project;
    });

    projects && projects.forEach(p => {
        let parentValue = p.parentProjectId;
        if (p.parentProjectId && !projectById[parentValue]) {
            addPermissonsRequiredProjectItem = true;
            parentValue = NestedItemsFractalRowStore.SentinelPermissionsRequiredId;
        }
        const item: ProjectTreeItem = {
            content: getContent(p.name),
            projectName: p.name,
            value: p.id.toString(),
            luid: p.luid,
            parentValue: parentValue?.toString()
        };
        projectTreeItems.push(item);
    });
    if (projectTreeItems && addPermissonsRequiredProjectItem) {
        projectTreeItems.push({
            content: getContent(Messages.permissionsRequiredProjectName()),
            projectName: Messages.permissionsRequiredProjectName(),
            value: NestedItemsFractalRowStore.SentinelPermissionsRequiredId,
            luid: '',
            disabled: true,
        });
    }
    return projectTreeItems;
}

const itemFilterFn = (item: ProjectTreeItem, searchText: string) => {
    return item.projectName.toLowerCase().indexOf(searchText.toLowerCase()) !== -1;
};

export function ProjectTree(): JSX.Element {
    const [projectTreeToggleState, setProjectTreeToggleState] = React.useState('off');
    const spaceInfo = Redux.useSelector(selectSpaceInfo);
    const layout = Redux.useSelector(selectLayoutType);
    const enabled = ProjectTreeService.instance.isProjectTreeEnabled(spaceInfo);

    React.useEffect(() => {
        let isMounted = true;

        if (enabled) {
            ProjectTreeService.instance.onProjectTreeToggleStateChanged.push(toggleState => {
                if (isMounted) {
                    setProjectTreeToggleState(toggleState);
                    if (toggleState === 'on') {
                        ProjectTreeService.instance.sendProjectTreeUsageTelemetryEvent('impression');
                    }
                }
            });
            ProjectTreeService.instance.initialize();
        }

        return () => {
            isMounted = false;
        };
    }, [enabled]);

    return enabled && projectTreeToggleState === 'on' && layout === 'desktop' && <ProjectTreeContent />;
}

function ProjectTreeContent(): JSX.Element {
    const siteName = getSiteName();
    const spaceInfo = Redux.useSelector(selectSpaceInfo);
    const selectedProjectId = spaceInfo?.spaceType === 'project' ? spaceInfo.item?.id : null;
    const urls = new SpacesSiteUrls({ siteUrlName: getSessionManagementServiceInstance().sessionSiteUrlName() });
    const { isLoading, destinationProjects } = ProjectHooks.useDestinationProjects(siteName);
    const projectTreeItems = useMemo(() => convertProjectsToProjectTreeItems(destinationProjects), [isLoading, destinationProjects]);
    const projectTreeRef = React.useRef<TreeInstance>(null);

    /**
     * Inline anonymous function is not used to prevent unnecessary re-renders in TreeWidget filtering
     * TreeWidget has a filter hook that updates the expansion state, which depends on itemFilterFn.
     * When a project is selected, it causes a re-render, and itemFilterFn is generated as a new function on every render.
     * This leads to the filter hook being triggered unnecessarily.
     */

    const handleSelectionChange = (selectedProjectId: string | null) => {
        if (selectedProjectId) {
            ProjectTreeService.instance.sendProjectTreeUsageTelemetryEvent('click_through');
            const selectedProjectTreeItem: ProjectTreeItem = projectTreeItems.find(item => item.value === selectedProjectId);
            Location.instance.setUrl(ProjectDefinition.contentSpaceUrl(urls, selectedProjectTreeItem.value, selectedProjectTreeItem.luid));
        }
    };

    React.useEffect(() => {
        const treeNodeExpandedValues = ProjectTreeService.instance.getTreeNodeExpandedValues();
        if (treeNodeExpandedValues && projectTreeRef.current) {
            projectTreeRef.current.expandedValues = treeNodeExpandedValues;
        }

        return () => {
            if (projectTreeRef.current) {
                ProjectTreeService.instance.saveTreeNodeExpandedValues(projectTreeRef.current.expandedValues);
            }
        };
    }, []);

    React.useEffect(() => {
        if (selectedProjectId && projectTreeRef.current && isLoading === false) {
            projectTreeRef.current.scrollIntoView(selectedProjectId, 'center');
        }
    }, [selectedProjectId, isLoading]);

    useEPT(projectTreeFeatureName, destinationProjects && destinationProjects.length > 0, destinationProjects && destinationProjects.length);
    return (
        <div className={projectTreeStyle}>
            <TreeWidget<ProjectTreeItem>
                itemFilterFn={itemFilterFn}
                items={projectTreeItems}
                onSelectionChange={(selectedProjectId: string | null) => handleSelectionChange(selectedProjectId)}
                onSearchTextChange={() => projectTreeRef.current.scrollIntoView(selectedProjectId, 'center')}
                selectedValue={selectedProjectId}
                style={{ height: '100%' }}
                treeRef={projectTreeRef}
                testId='ProjectTree'
                label={Messages.searchProjects()}
            />
        </div>
    );
}